import type { ParameterDirection, SweetSpotParameter, SweetSpotParameterConfig } from './types';
import {
  DEFAULT_GRADE_THRESHOLDS, evaluateRelativeMethod,
  registerEvaluationMethod, stableDefinitionHash,
} from './sweetSpotEvaluationEngine';
import type {
  EvaluationMethodConfig, EvaluationMethodDefinition, EvaluationMethodKey,
  PhysicalAnchor,
} from './sweetSpotEvaluationEngine';
import type { DataQualityReport, EvaluationMethodDocumentation } from './sweetSpotEvaluationEngine';
import { normalizeWeights } from './sweetSpotUtils';

const parameter = (
  id: string,
  key: SweetSpotParameter['key'],
  group: SweetSpotParameter['group'],
  label: string,
  unit: string,
  weight: number,
  direction: ParameterDirection,
  enabled = true,
): SweetSpotParameter => ({ id, key, group, label, unit, weight, direction, enabled });

const LEGACY_PARAMETERS: SweetSpotParameterConfig = {
  rq: [
    parameter('rq.toc', 'toc', 'rq', 'TOC（总有机碳）', '%', 0.35, 'larger_is_better'),
    parameter('rq.porosity', 'porosity', 'rq', '孔隙度', '%', 0.30, 'larger_is_better'),
    parameter('rq.brittleness', 'brittleness', 'rq', 'BI（脆性指数）', '%', 0.20, 'larger_is_better'),
    parameter('rq.clayContent', 'clayContent', 'rq', '黏土含量', '%', 0.15, 'smaller_is_better', false),
  ],
  cq: [
    parameter('cq.youngModulus', 'youngModulus', 'cq', 'YM（杨氏模量）', 'GPa', 0.35, 'larger_is_better'),
    parameter('cq.poissonRatio', 'poissonRatio', 'cq', 'PR（泊松比）', '无量纲', 0.25, 'smaller_is_better'),
    parameter('cq.brittleness', 'brittleness', 'cq', 'BI（脆性指数）', '%', 0.20, 'larger_is_better'),
    parameter('cq.minHorizontalStress', 'minHorizontalStress', 'cq', 'Shmin（最小水平主应力）', 'MPa', 0.20, 'smaller_is_better'),
  ],
};

const cloneConfig = (config: SweetSpotParameterConfig): SweetSpotParameterConfig => ({
  rq: config.rq.map((item) => ({ ...item })),
  cq: config.cq.map((item) => ({ ...item })),
});
const LEGACY_DOCUMENTATION: EvaluationMethodDocumentation = {
  displayName: '甜点指数 SSI',
  shortName: 'SSI',
  origin: 'legacy',
  badges: ['SSI', '统一评价', '井段内相对评价'],
  summary: '统一计算储层品质 RQ、工程品质 CQ 与甜点指数 SSI，并按固定阈值形成甜点等级。',
  principle: '在当前评价井段内按指标进行min–max归一化，再按点位可用指标重归一化权重，分别计算RQ与CQ并等权合成SSI。',
  scoreNature: 'relative',
  formulaSections: [
    {
      title: '评价井段统计范围',
      expression: 'x_j,min = min_i(x_ij)\nx_j,max = max_i(x_ij)',
      explanation: '每个指标的最小值和最大值均来自本次评价井段，不是固定物理标准。',
      variables: ['x_ij：深度点i处指标j的原始值', 'x_j,min / x_j,max：本次评价井段内指标j的最小值 / 最大值'],
    },
    {
      title: '正向指标',
      expression: 's_ij = clip[(x_ij - x_j,min) / (x_j,max - x_j,min), 0, 1]',
      explanation: '指标越大越好；超出统计范围的结果截断到0～1。',
      variables: ['s_ij：深度点i处指标j的标准化评分', 'clip：将结果限制在0～1'],
    },
    {
      title: '负向指标',
      expression: 's_ij = clip[(x_j,max - x_ij) / (x_j,max - x_j,min), 0, 1]',
      explanation: '指标越小越好；超出统计范围的结果截断到0～1。',
    },
    {
      title: '常量列',
      expression: '当 x_j,max = x_j,min 时，s_ij = 0.5',
      explanation: '井段内没有变化的指标采用中性分数，避免除零。',
    },
    {
      title: '有效权重重归一化',
      expression: 'w̃_ij = w_j / Σ_(k∈A_i) w_k',
      explanation: 'A_i为深度点i处实际可用且已启用的指标集合；缺失指标不进入该点计算。若全部有效权重为零，则使用等权重。',
      variables: ['w_j：配置权重', 'w̃_ij：深度点i处重新归一化后的有效权重', 'A_i：该点可用指标集合'],
    },
    {
      title: '分组评价',
      expression: 'RQ_i = Σ_(j∈RQ) w̃_ij s_ij\nCQ_i = Σ_(j∈CQ) w̃_ij s_ij',
      explanation: 'RQ与CQ在各自启用且可用的指标内独立加权。',
    },
    {
      title: '综合评价与单组回退',
      expression: 'SSI_i = 0.5 RQ_i + 0.5 CQ_i\n仅RQ可用：SSI_i = RQ_i；仅CQ可用：SSI_i = CQ_i',
      explanation: '两组均存在时等权算术平均；单组缺失时兼容原始行为，退化为另一组评分。',
    },
    {
      title: '等级划分',
      expression: 'I：SSI ≥ 0.80\nII：0.60 ≤ SSI < 0.80\nIII：0.40 ≤ SSI < 0.60\nIV：0.20 ≤ SSI < 0.40\nV：SSI < 0.20',
      explanation: '默认阈值对应 I～V 级。',
    },
  ],
  indicatorGroups: [
    { group: 'RQ', indicators: ['TOC', '孔隙度', '脆性指数', '黏土含量（可选）'], source: '黏土含量不是所有储层的必需输入，默认关闭；仅在具有可靠矿物解释且工程上需要考虑水敏/泥质影响时启用。' },
    { group: 'CQ', indicators: ['杨氏模量', '泊松比', '脆性指数', '最小水平主应力'], source: 'FracSight原始参数配置；保留脆性在CQ中的历史定义。' },
  ],
  weightingDescription: '支持预设权重、自定义权重与层次分析（AHP）；每个深度点按实际可用指标重新归一化，零权重总和时自动等权。',
  aggregationDescription: 'RQ与CQ默认各占50%；单组缺失时回退为另一组评分。',
  missingDataDescription: '缺失指标逐点剔除并重归一化剩余权重；RQ或CQ整组缺失时仍显示另一组回退SSI，但需显式允许后才能设为当前。',
  gradingDescription: '按0.80、0.60、0.40、0.20四个阈值划分I～V类。',
  dataRequirements: ['至少一个已启用的RQ或CQ指标存在有效数据。', '跨井复算时需保留相同输入曲线、评价井段、参数方向和权重配置。'],
  advantages: ['完整兼容原始结果', '计算快速', '逻辑简单', '易于解释', '数据缺失时仍可回退计算'],
  limitations: ['依赖当前评价井段min–max', '属于井段内相对评价', '不能直接进行跨井、跨区块绝对比较', '对极端值敏感', '不同深度点可能使用不同有效指标', '脆性同时参与RQ和CQ时需要关注重复计权风险'],
  recommendedFor: ['复现并读取既有评价结果', '同一井段内快速筛选相对高低段', '需要兼容既有下游流程的场景'],
  notRecommendedFor: ['跨井或跨区块绝对排名', '需要固定物理阈值审计的正式评价', '对极端值高度敏感的数据集'],
  references: [
    { label: 'FracSight SSI 计算口径', description: '保持既有 RQ、CQ、SSI 与甜点分级计算结果兼容。' },
    { label: '实现边界', description: 'FracSight具体评分函数、权重配置和组合方式属于本项目实现。商业软件未公开的内部公式不作为实现依据。' },
  ],
  comparison: {
    identity: '原始兼容', scoreNature: '井段内相对', normalization: '当前井段min–max',
    weighting: '推荐/自定义/AHP', brittlenessPlacement: 'RQ和CQ', aggregation: '算术平均',
    singleGroupMissing: '回退到另一组', crossWellComparison: '不建议',
    mainAdvantage: '兼容、简单', mainLimitation: '相对性、重复计权',
  },
};

const definition = (
  value: Omit<EvaluationMethodDefinition, 'definitionHash'>,
): EvaluationMethodDefinition => {
  const { documentation, ...algorithmDefinition } = value;
  return { ...value, documentation, definitionHash: stableDefinitionHash(algorithmDefinition) };
};

export const LEGACY_METHOD = definition({
  methodId: 'legacy-rule', version: '1.0.0', name: '甜点指数 SSI', scope: 'relative',
  scopeLabel: '井段内相对评分', indicatorDefinitions: cloneConfig(LEGACY_PARAMETERS),
  scoringStrategy: '当前评价井段 min–max；常量列记0.5；脆性历史兼容地同时进入RQ和CQ。',
  weightStrategy: '预设权重、自定义权重或层次分析（AHP）；逐点缺失时对剩余指标权重重新归一化。',
  combinationStrategy: 'RQ、CQ均存在时算术平均；单组缺失时回退到另一组。',
  gradingStrategy: '固定阈值：0.80/0.60/0.40/0.20。',
  requiredIndicators: [], optionalIndicators: [...LEGACY_PARAMETERS.rq, ...LEGACY_PARAMETERS.cq].map((item) => item.id),
  missingStrategy: 'legacy_group_fallback',
  applicability: '复现当前井段内的既有评价行为，供旧结果和下游流程兼容。',
  limitations: ['不能用于跨井、跨区块绝对比较。', '缺少输入数据版本和归一化范围快照的旧结果无法严格复算。'],
  references: ['FracSight 现行前端规则（冻结于 legacy-rule@1.0.0）'],
  documentation: LEGACY_DOCUMENTATION,
});

let registered = false;
export function ensureSweetSpotMethodsRegistered(): void {
  if (registered) return;
  registerEvaluationMethod({ definition: LEGACY_METHOD, evaluate: (points, config, method) => (
    evaluateRelativeMethod(points, config, method, true)
  ) });
  registered = true;
}

function emptyAnchor(parameter: SweetSpotParameter): PhysicalAnchor {
  return {
    parameterId: parameter.id,
    scoringType: parameter.direction === 'smaller_is_better' ? 'negative_linear'
      : parameter.direction === 'target_range' ? 'target_range' : 'positive_linear',
    confirmed: false,
  };
}

export function createDefaultMethodConfig(
  methodKey: EvaluationMethodKey,
  legacyParameters?: SweetSpotParameterConfig,
): EvaluationMethodConfig {
  ensureSweetSpotMethodsRegistered();
  if (methodKey !== 'legacy-rule@1.0.0') throw new Error(`当前仅支持甜点指数 SSI：${methodKey}`);
  const parameters = legacyParameters
    ? cloneConfig(legacyParameters) : cloneConfig(LEGACY_METHOD.indicatorDefinitions);
  return {
    parameters,
    anchors: Object.fromEntries([...parameters.rq, ...parameters.cq].map((item) => [item.id, emptyAnchor(item)])),
    weightStrategy: 'fixed',
    combination: 'arithmetic_mean',
    gradeThresholds: { ...DEFAULT_GRADE_THRESHOLDS },
    allowLegacySingleGroupActivation: false,
    reservoirType: undefined,
    templateVersion: undefined,
    reviewState: undefined,
  };
}

ensureSweetSpotMethodsRegistered();

export type EvaluationMethodRuntimeDocumentation = {
  actualFormula: string;
  scoreNatureLabel: string;
  currentWeighting: string;
  currentGrading: string;
  dataStatus: string;
  saveStatus: string;
  primaryRisk: string;
  relativeWarning?: string;
  enabledIndicators: { rq: string[]; cq: string[] };
  effectiveWeights: { rq: string[]; cq: string[] };
  ahp: Array<{
    group: 'RQ' | 'CQ';
    available: boolean;
    applied: boolean;
    lambdaMax?: number;
    ci?: number;
    cr?: number;
    consistent?: boolean;
    weights: string[];
  }>;
  anchorConfigurations: string[];
};

function currentGroupWeights(
  group: 'rq' | 'cq',
  config: EvaluationMethodConfig,
): string[] {
  const enabled = config.parameters[group].filter((item) => item.enabled);
  const ahp = config.ahp?.[group];
  const useAhp = config.weightStrategy === 'ahp' && ahp?.consistent && ahp.weights.length === enabled.length;
  const normalized = useAhp
    ? Object.fromEntries(enabled.map((item, index) => [item.id, ahp.weights[index]]))
    : normalizeWeights(enabled);
  return enabled.map((item) => `${item.label}：${((normalized[item.id] ?? 0) * 100).toFixed(1)}%`);
}

export function buildMethodRuntimeDocumentation(
  definition: EvaluationMethodDefinition,
  config: EvaluationMethodConfig,
  quality: DataQualityReport,
): EvaluationMethodRuntimeDocumentation {
  const enabledIndicators = {
    rq: config.parameters.rq.filter((item) => item.enabled).map((item) => `${item.label}（${item.unit}）`),
    cq: config.parameters.cq.filter((item) => item.enabled).map((item) => `${item.label}（${item.unit}）`),
  };
  const actualFormula = 'SSI = 0.5 RQ + 0.5 CQ；单组缺失时 SSI 使用另一组评分';
  const strategyLabel = config.weightStrategy === 'ahp'
    ? '层次分析（AHP）'
    : config.weightStrategy === 'fixed'
      ? '预设权重' : '自定义权重';
  const invalidAhp = config.weightStrategy === 'ahp' && (['rq', 'cq'] as const).some((group) => {
    const enabledCount = config.parameters[group].filter((item) => item.enabled).length;
    const result = config.ahp?.[group];
    return enabledCount > 0 && (!result?.consistent || result.weights.length !== enabledCount);
  });
  const currentWeighting = invalidAhp
    ? '已选择AHP，但至少一组尚未通过一致性/维度校验；当前预览回退为配置权重逐点重归一化，不能正式保存。'
    : `${strategyLabel}；组内归一化，点位缺失时按实际可用指标再次归一化。`;
  const thresholds = config.gradeThresholds;
  const currentGrading = `I≥${thresholds.classI.toFixed(2)}；II≥${thresholds.classII.toFixed(2)}；III≥${thresholds.classIII.toFixed(2)}；IV≥${thresholds.classIV.toFixed(2)}；其余为V`;
  const dataStatus = `完整度${(quality.completeness * 100).toFixed(0)}%；RQ${quality.rqAvailable ? "可用" : "不足"}，CQ${quality.cqAvailable ? "可用" : "不足"}；${quality.availableIndicatorIds.length}个可用指标`;
  const primaryRisk = quality.issues.find((issue) => issue.severity === 'error')?.message
    ?? definition.documentation.limitations[0];
  const scoreNatureLabel = '井段内相对评价';
  const effectiveWeights = {
    rq: currentGroupWeights('rq', config),
    cq: currentGroupWeights('cq', config),
  };
  const ahp = (['rq', 'cq'] as const).map((group) => {
    const result = config.ahp?.[group];
    return {
      group: group.toUpperCase() as 'RQ' | 'CQ',
      available: Boolean(result),
      applied: Boolean(config.weightStrategy === 'ahp' && result?.consistent
        && result.weights.length === config.parameters[group].filter((item) => item.enabled).length),
      lambdaMax: result?.lambdaMax,
      ci: result?.ci,
      cr: result?.cr,
      consistent: result?.consistent,
      weights: result?.weights.map((weight, index) => {
        const parameter = config.parameters[group].filter((item) => item.enabled)[index];
        return `${parameter?.label ?? `指标${index + 1}`}：${(weight * 100).toFixed(1)}%`;
      }) ?? [],
    };
  });
  return {
    actualFormula,
    scoreNatureLabel,
    currentWeighting,
    currentGrading,
    dataStatus,
    saveStatus: quality.canSaveFormal ? '允许保存为正式结果' : '当前不允许保存为正式结果',
    primaryRisk,
    relativeWarning: '当前结果依赖本次评价井段统计范围，属于相对评分，不建议直接跨井或跨区块比较。',
    enabledIndicators,
    effectiveWeights,
    ahp,
    anchorConfigurations: [],
  };
}
